import type { License } from '@/types/license';

export const LICENSE_STORAGE_KEY = 'app_local_license';
const LEGACY_LICENSE_SESSION_KEY = 'app_session_license';
const LICENSE_INIT_SESSION_PREFIX = 'license_init_called:';

type LicenseStorageCache = {
    tenantID: string;
    license: License | null;
};

const parseLicenseStorage = (raw: string): LicenseStorageCache | null => {
    try {
        const parsed = JSON.parse(raw) as LicenseStorageCache & {
            licenses?: Record<string, License | null>;
        };
        if (!parsed?.tenantID) return null;

        if ('license' in parsed && !('licenses' in parsed)) {
            return { tenantID: parsed.tenantID, license: parsed.license ?? null };
        }

        if (parsed.licenses && typeof parsed.licenses === 'object') {
            const first = Object.values(parsed.licenses).find(Boolean);
            return { tenantID: parsed.tenantID, license: (first as License | undefined) ?? null };
        }

        return null;
    } catch {
        return null;
    }
};

export const readLicenseFromStorage = (tenantID: string): License | null | undefined => {
    try {
        let raw = localStorage.getItem(LICENSE_STORAGE_KEY);
        if (!raw) {
            raw = sessionStorage.getItem(LEGACY_LICENSE_SESSION_KEY);
            if (raw) {
                localStorage.setItem(LICENSE_STORAGE_KEY, raw);
                sessionStorage.removeItem(LEGACY_LICENSE_SESSION_KEY);
            }
        }
        if (!raw) return undefined;

        const parsed = parseLicenseStorage(raw);
        if (!parsed || parsed.tenantID !== tenantID) return undefined;
        return parsed.license ?? null;
    } catch {
        return undefined;
    }
};

export const saveLicenseToStorage = (tenantID: string, license: License | null) => {
    try {
        localStorage.setItem(
            LICENSE_STORAGE_KEY,
            JSON.stringify({ tenantID, license } satisfies LicenseStorageCache),
        );
        sessionStorage.removeItem(LEGACY_LICENSE_SESSION_KEY);
    } catch {
        // ignore quota / private mode
    }
};

export const getLicenseInitParam = (tenantID: string): '0' | '1' => {
    if (!tenantID) return '0';
    try {
        const key = `${LICENSE_INIT_SESSION_PREFIX}${tenantID}`;
        if (sessionStorage.getItem(key)) return '0';
        sessionStorage.setItem(key, '1');
        return '1';
    } catch {
        return '1';
    }
};

export const clearLicenseInitFlag = () => {
    try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key?.startsWith(LICENSE_INIT_SESSION_PREFIX)) {
                sessionStorage.removeItem(key);
            }
        }
    } catch {
        // ignore
    }
};

export const clearLicenseStorage = () => {
    try {
        localStorage.removeItem(LICENSE_STORAGE_KEY);
        sessionStorage.removeItem(LEGACY_LICENSE_SESSION_KEY);
        clearLicenseInitFlag();
    } catch {
        // ignore
    }
};
